import React, { useState } from 'react';
import { Menu, Button, Drawer, Badge } from 'antd';
import {
  BugOutlined,
  CloudDownloadOutlined,
  MenuOutlined,
  CloseOutlined,
  QuestionCircleOutlined,
  EnvironmentOutlined,
  SyncOutlined,
  InboxOutlined,
  ContainerOutlined,
  CustomerServiceOutlined,
  WarningOutlined,
  ApiOutlined,
  TableOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import './Main.scss';
import { ReactSVG } from 'react-svg';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { openAdminGuide } from '../utils/openGuide';
import useMonitoringCounts from '../hooks/useMonitoringCounts';
import { tenantConfig } from '../config/tenantConfig';

const MainMenu = ({ user, avatarUrl }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { t, i18n } = useTranslation('menu');

  // Open the admin guide in the operator's current app language. The
  // guide is served by the backend behind an ADMIN/USER guard (not a
  // public static file), so we fetch-with-auth + open rather than link.
  const guideLang = (i18n?.language || 'en').toLowerCase().startsWith('fr') ? 'fr' : 'en';

  const role = user?.role?.toLowerCase();
  // Red issue-count badges on the Monitoring items. Only operators/admins poll.
  const monitoringEnabled = role === 'admin' || role === 'user' || role === 'superadmin';
  const monitoringCounts = useMonitoringCounts(monitoringEnabled);
  const monitoringTotal =
    monitoringCounts.inconsistencies +
    monitoringCounts.webhookErrors +
    monitoringCounts.airtableErrors +
    monitoringCounts.openTickets +
    monitoringCounts.intakeRefusals +
    monitoringCounts.integrity;

  // Map the current path to a single menu key. Uses startsWith on the full
  // `/admin/...` prefix so the GLOBAL Sites/Recurring/Collected (/admin/sites)
  // don't collide with the partner-scoped ones (/admin/partners/:id/sites →
  // matches /admin/partners → Partners). With grouped dropdowns, AntD also
  // marks the parent group active when a child key is selected.
  const keyFor = (p) => {
    if (p.startsWith('/admin/route-plans')) return '/route-plans';
    if (p.startsWith('/admin/routes')) return '/routes';
    if (p.startsWith('/admin/sites')) return '/sites';
    if (p.startsWith('/admin/recurring')) return '/recurring';
    if (p.startsWith('/admin/collection-points')) return '/collection-points';
    if (p.startsWith('/admin/collected')) return '/collected';
    if (p.startsWith('/admin/orders')) return '/orders';
    if (p.startsWith('/admin/hubs')) return '/hubs';
    if (p.startsWith('/admin/fleet')) return '/fleet';
    if (p.startsWith('/admin/drivers')) return '/drivers';
    if (p.startsWith('/admin/scheduler')) return '/scheduler';
    if (p.startsWith('/admin/partners')) return '/partners';
    if (p.startsWith('/admin/stats')) return '/stats';
    if (p.startsWith('/admin/tickets')) return '/tickets';
    if (p.startsWith('/admin/inconsistencies')) return '/inconsistencies';
    if (p.startsWith('/admin/webhook-errors')) return '/webhook-errors';
    if (p.startsWith('/admin/airtable-errors')) return '/airtable-errors';
    if (p.startsWith('/admin/integrity')) return '/integrity';
    if (p.startsWith('/admin/api-intake')) return '/api-intake';
    if (p.startsWith('/admin/diagnostics')) return '/diagnostics';
    if (p.startsWith('/admin/users')) return '/users';
    return p;
  };
  const selectedKey = keyFor(pathname);

  // OVO-133 — Vercel-style grouped nav. The flat row ran out of horizontal
  // space, so items collapse into a few dropdown groups. `icon` is either an
  // SVG path (ReactSVG) or an AntD icon node. Leaf = a navigable child.
  const renderLabel = (icon, to, label) => (
    <div className="MenuItemContent">
      {typeof icon === 'string' ? (
        <ReactSVG className="icon" src={icon} />
      ) : (
        React.cloneElement(icon, { className: 'icon' })
      )}
      <Link to={to}>{label}</Link>
    </div>
  );
  const leaf = (key, icon, to, label) => ({ key, label: renderLabel(icon, to, label) });

  // A red count badge for Monitoring items (menu issue indicator).
  const countBadge = (count) =>
    count > 0 ? <Badge count={count} size="small" overflowCount={99} style={{ marginLeft: 6 }} /> : null;
  const monitoringLeaf = (key, icon, to, label, count) => ({
    key,
    label: (
      <div className="MenuItemContent">
        {React.cloneElement(icon, { className: 'icon' })}
        <Link to={to}>{label}</Link>
        {countBadge(count)}
      </div>
    ),
  });

  // The primary operator workflow stays a FLAT top-level row (as it was before
  // OVO-133's grouped dropdowns — operators found the flat menu faster). The
  // newer / less-frequent items (Sites, Recurring, Collected) and the whole
  // Monitoring section collapse into a single "More" dropdown, which carries
  // the aggregated monitoring issue badge so the red dot is still visible.
  const items = [
    leaf('/route-plans', '/icons/route-plans.svg', '/admin/route-plans', t('routePlan')),
    leaf('/routes', '/icons/routes.svg', '/admin/routes', t('routes')),
    leaf('/scheduler', '/icons/event_note.svg', '/admin/scheduler', t('scheduler')),
    leaf('/orders', '/icons/all_inbox.svg', '/admin/orders', t('orders')),
    leaf('/hubs', '/icons/all_inbox.svg', '/admin/hubs', t('hubs')),
    leaf('/fleet', '/icons/directions_car.svg', '/admin/fleet', t('fleet')),
    leaf('/drivers', '/icons/directions_bike.svg', '/admin/drivers', t('drivers')),
    // OVO-132 — the Partners management page is multi-partner chrome; hidden in
    // a single-tenant instance (one implicit partner, never user-visible).
    ...(tenantConfig.isSingleTenant
      ? []
      : [leaf('/partners', '/icons/all_inbox.svg', '/admin/partners', t('partners'))]),
    leaf('/stats', '/icons/all_inbox.svg', '/admin/stats', t('stats')),
    {
      key: 'grp-more',
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          {t('groupMore')}
          {countBadge(monitoringTotal)}
        </span>
      ),
      children: [
        leaf('/sites', <EnvironmentOutlined />, '/admin/sites', t('sites')),
        leaf('/collection-points', <ContainerOutlined />, '/admin/collection-points', t('collection-points')),
        leaf('/recurring', <SyncOutlined />, '/admin/recurring', t('recurring')),
        leaf('/collected', <InboxOutlined />, '/admin/collected', t('collected')),
        { type: 'divider' },
        // OVO-117 — the support queue; badge = OPEN tickets.
        monitoringLeaf(
          '/tickets',
          <CustomerServiceOutlined />,
          '/admin/tickets',
          t('tickets'),
          monitoringCounts.openTickets,
        ),
        monitoringLeaf(
          '/inconsistencies',
          <WarningOutlined />,
          '/admin/inconsistencies',
          t('inconsistencies'),
          monitoringCounts.inconsistencies,
        ),
        monitoringLeaf(
          '/webhook-errors',
          <ApiOutlined />,
          '/admin/webhook-errors',
          t('webhookErrors'),
          monitoringCounts.webhookErrors,
        ),
        monitoringLeaf(
          '/airtable-errors',
          <TableOutlined />,
          '/admin/airtable-errors',
          t('airtableErrors'),
          monitoringCounts.airtableErrors,
        ),
        // #149 — what the order state machine and the route-step checks found.
        monitoringLeaf(
          '/integrity',
          <SafetyCertificateOutlined />,
          '/admin/integrity',
          t('integrity'),
          monitoringCounts.integrity,
        ),
        // API intake — the ledger carries the planner's upload verdicts since
        // OVO-292: the badge = orders a partner sent that the planner REFUSED
        // and that still don't exist (the retry-proof silent loss).
        monitoringLeaf(
          '/api-intake',
          <CloudDownloadOutlined />,
          '/admin/api-intake?view=refusals',
          t('apiIntake'),
          monitoringCounts.intakeRefusals,
        ),
        // Diagnostics — a lookup tool, not an error queue: no badge.
        leaf('/diagnostics', <BugOutlined />, '/admin/diagnostics', t('diagnostics')),
      ],
    },
    ...(role === 'superadmin' ? [leaf('/users', '/icons/supervised_user_circle.svg', '/admin/users', t('users'))] : []),
    {
      // The admin guide is served by the backend behind an ADMIN/USER
      // guard — not a public static file and not a React route. The
      // click handler fetches it with auth and opens it (see openGuide).
      key: '/help',
      label: (
        <div className="MenuItemContent" onClick={() => openAdminGuide(guideLang)} style={{ cursor: 'pointer' }}>
          <QuestionCircleOutlined className="icon" />
          <span style={{ fontSize: 14, lineHeight: '24px', color: '#231f20' }}>{t('help')}</span>
        </div>
      ),
    },
  ];

  const drawerItems = [
    { label: t('routePlan'), path: '/admin/route-plans', key: '/route-plans', icon: '/icons/route-plans.svg' },
    { label: t('routes'), path: '/admin/routes', key: '/routes', icon: '/icons/routes.svg' },
    { label: t('scheduler'), path: '/admin/scheduler', key: '/scheduler', icon: '/icons/event_note.svg' },
    { label: t('orders'), path: '/admin/orders', key: '/orders', icon: '/icons/all_inbox.svg' },
    // OVO-133 — Sites / Recurring / Collected now top-level.
    { label: t('sites'), path: '/admin/sites', key: '/sites', iconNode: <EnvironmentOutlined /> },
    {
      label: t('collection-points'),
      path: '/admin/collection-points',
      key: '/collection-points',
      iconNode: <ContainerOutlined />,
    },
    { label: t('recurring'), path: '/admin/recurring', key: '/recurring', iconNode: <SyncOutlined /> },
    { label: t('hubs'), path: '/admin/hubs', key: '/hubs', icon: '/icons/all_inbox.svg' },
    { label: t('fleet'), path: '/admin/fleet', key: '/fleet', icon: '/icons/directions_car.svg' },
    { label: t('drivers'), path: '/admin/drivers', key: '/drivers', icon: '/icons/directions_bike.svg' },
    ...(tenantConfig.isSingleTenant
      ? []
      : [{ label: t('partners'), path: '/admin/partners', key: '/partners', icon: '/icons/all_inbox.svg' }]),
    { label: t('collected'), path: '/admin/collected', key: '/collected', iconNode: <InboxOutlined /> },
    { label: t('stats'), path: '/admin/stats', key: '/stats', icon: '/icons/all_inbox.svg' },
    {
      label: t('inconsistencies'),
      path: '/admin/inconsistencies',
      key: '/inconsistencies',
      iconNode: <WarningOutlined />,
      badge: monitoringCounts.inconsistencies,
    },
    {
      label: t('webhookErrors'),
      path: '/admin/webhook-errors',
      key: '/webhook-errors',
      iconNode: <ApiOutlined />,
      badge: monitoringCounts.webhookErrors,
    },
    {
      label: t('airtableErrors'),
      path: '/admin/airtable-errors',
      key: '/airtable-errors',
      iconNode: <TableOutlined />,
      badge: monitoringCounts.airtableErrors,
    },
    {
      label: t('integrity'),
      path: '/admin/integrity',
      key: '/integrity',
      iconNode: <SafetyCertificateOutlined />,
      badge: monitoringCounts.integrity,
    },
    {
      label: t('apiIntake'),
      path: '/admin/api-intake',
      key: '/api-intake',
      iconNode: <CloudDownloadOutlined />,
      badge: monitoringCounts.intakeRefusals,
    },
    { label: t('diagnostics'), path: '/admin/diagnostics', key: '/diagnostics', iconNode: <BugOutlined /> },
    ...(role === 'superadmin'
      ? [{ label: t('users'), path: '/admin/users', key: '/users', icon: '/icons/supervised_user_circle.svg' }]
      : []),
    // Guarded doc — fetched-with-auth + opened (see `guide`), not routed.
    { label: t('help'), key: '/help', icon: null, guide: true },
  ];

  const handleDrawerNav = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleDrawerItem = (item) => {
    if (item.guide) {
      openAdminGuide(guideLang);
      setDrawerOpen(false);
      return;
    }
    handleDrawerNav(item.path);
  };

  const { t: tc } = useTranslation('common');

  return (
    <>
      <Menu className="MainMenu desktop-menu" selectedKeys={[selectedKey]} mode="horizontal" items={items} />

      <Button className="mobile-menu-trigger" type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} />

      <Drawer
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
        className="mobile-nav-drawer"
        closeIcon={<CloseOutlined />}
        title={<img src="/logo.svg" alt="logo" style={{ height: 28 }} />}
      >
        {user && (
          <div className="mobile-nav-user">
            <img className="mobile-nav-user-avatar" src={avatarUrl} alt="" />
            <div className="mobile-nav-user-info">
              <div className="mobile-nav-user-name">{user.name}</div>
              <div className="mobile-nav-user-email">{user.email}</div>
            </div>
          </div>
        )}
        <nav className="mobile-nav-list">
          {drawerItems.map((item) => (
            <div
              key={item.key}
              className={`mobile-nav-item ${selectedKey === item.key ? 'active' : ''}`}
              onClick={() => handleDrawerItem(item)}
            >
              {item.icon ? (
                <ReactSVG className="icon" src={item.icon} />
              ) : item.iconNode ? (
                React.cloneElement(item.iconNode, { className: 'icon', style: { fontSize: 18 } })
              ) : (
                <QuestionCircleOutlined className="icon" style={{ fontSize: 18 }} />
              )}
              <span>{item.label}</span>
              {item.badge > 0 && (
                <Badge count={item.badge} size="small" overflowCount={99} style={{ marginLeft: 'auto' }} />
              )}
            </div>
          ))}
        </nav>
        <div className="mobile-nav-footer">
          <LanguageSwitcher compact />
          <div className="mobile-nav-item" onClick={() => handleDrawerNav('/logout')}>
            <span>{tc('logOut')}</span>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default MainMenu;
